/**
 * User Management API Service
 *
 * Comprehensive API for managing sub-users, roles, permissions, and access control
 * Implements complete RBAC (Role-Based Access Control) system
 */

import {post, get} from './client';
import CryptoJS from "crypto-js";
import {KEY} from "../../Constants";

// ==================== SUB-USER MANAGEMENT ====================

// Helper function to get decrypted data
const getOriginalText = () => {
    const encryptedData = localStorage.getItem("myMap");
    return encryptedData
        ? CryptoJS.AES.decrypt(encryptedData, KEY).toString(CryptoJS.enc.Utf8)
        : "";
};

export const userManagementApi = {
    /**
     * Sub-user login
     * @param {Object} credentials
     * @param {string} credentials.email_address - Email address
     * @param {string} credentials.password - Password
     * @returns {Promise<Object>} Login response with user details
     */
    subUserLogin: async (credentials) => {
        return post('pegasus/visionary/userManagement/login', credentials);
    },

    /**
     * Get all sub-users for a landlord
     * @param {string} landlordId - Landlord ID
     * @returns {Promise<Object>} List of sub-users
     */
    getSubUsers: async (landlordId) => {
        const originalText = getOriginalText();

        return post('pegasus/visionary/userManagement/sub-users/get', {
                landlord_id: landlordId,
            },
            {
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                }
            });
    },

    /**
     * Create a new sub-user
     * @param {Object} userData
     * @param {string} userData.landlord_id - Landlord ID
     * @param {string} userData.first_name - First name
     * @param {string} userData.last_name - Last name
     * @param {string} userData.email - Email
     * @param {string} userData.phone_number - Phone number
     * @param {string} userData.role_id - Role ID
     * @param {string} userData.password - Initial password
     * @returns {Promise<Object>} Create response
     */
    createSubUser: async (userData) => {
        const originalText = getOriginalText();

        return post('pegasus/visionary/userManagement/sub-users/create', userData,
            {
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                }
            });
    },

    /**
     * Update a sub-user's information
     * @param {Object} userData
     * @param {string} userData.sub_user_id - Sub-User ID
     * @param {string} userData.landlord_id - Landlord ID
     * @param {string} userData.first_name - First name
     * @param {string} userData.last_name - Last name
     * @param {string} userData.email - Email
     * @param {string} userData.phone_number - Phone number
     * @param {string} userData.role_id - Role ID
     * @returns {Promise<Object>} Update response
     */
    updateSubUser: async (userData) => {
        const originalText = getOriginalText();

        return post('pegasus/visionary/userManagement/sub-users/update', userData,
            {
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                }
            });
    },

    /**
     * Update sub-user status (active/inactive)
     * @param {Object} statusData
     * @param {string} statusData.sub_user_id - Sub-User ID
     * @param {string} statusData.landlord_id - Landlord ID
     * @param {string} statusData.user_status - New status ('ACTIVE' or 'INACTIVE')
     * @returns {Promise<Object>} Update response
     */
    updateSubUserStatus: async (statusData) => {
        const originalText = getOriginalText();

        return post('pegasus/visionary/userManagement/sub-users/update/status', statusData,
            {
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                }
            });
    },

    /**
     * Delete a sub-user
     * @param {Object} deleteData
     * @param {string} deleteData.sub_user_id - Sub-User ID to delete
     * @param {string} deleteData.landlord_id - Landlord ID
     * @returns {Promise<Object>} Delete response
     */
    deleteSubUser: async (deleteData) => {
        const originalText = getOriginalText();

        return post('pegasus/visionary/userManagement/sub-users/delete', deleteData,
            {
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                }
            });
    },
};

// ==================== PROPERTY ACCESS MANAGEMENT ====================

export const propertyAccessApi = {
    /**
     * Grant property access to a sub-user
     * @param {Object} accessData
     * @param {string} accessData.sub_user_id - Sub-User ID
     * @param {string} accessData.landlord_id - Landlord ID
     * @param {number} accessData.property_id - Property ID (single property)
     * @param {number} accessData.role_id - Role ID for this property access
     * @returns {Promise<Object>} Grant access response
     */
    grantAccess: async (accessData) => {
        const originalText = getOriginalText();

        return post('pegasus/visionary/userManagement/property-access/grant', accessData,
            {
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                }
            });
    },

    /**
     * Revoke property access from a sub-user
     * @param {Object} accessData
     * @param {string} accessData.sub_user_id - Sub-User ID
     * @param {string} accessData.landlord_id - Landlord ID
     * @param {Array<string>} accessData.property_ids - Array of property IDs to revoke
     * @returns {Promise<Object>} Revoke access response
     */
    revokeAccess: async (accessData) => {
        const originalText = getOriginalText();

        return post('pegasus/visionary/userManagement/property-access/revoke', accessData,
            {
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                }
            });
    },

    /**
     * Get all properties a user has access to
     * @param {Object} accessData
     * @param {string} accessData.sub_user_id - Sub-User ID
     * @param {string} accessData.landlord_id - Landlord ID
     * @returns {Promise<Object>} List of accessible properties
     */
    getUserAccess: async (accessData) => {
        const originalText = getOriginalText();

        return post('pegasus/visionary/userManagement/property-access/get', accessData,
            {
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                }
            });
    },

    /**
     * Check if user has specific permission for a property
     * @param {Object} checkData
     * @param {string} checkData.sub_user_id - Sub-User ID
     * @param {string} checkData.landlord_id - Landlord ID
     * @param {string} checkData.property_id - Property ID
     * @param {string} checkData.permission - Permission to check
     * @returns {Promise<Object>} Permission check result
     */
    checkPermission: async (checkData) => {
        const originalText = getOriginalText();

        return post('pegasus/visionary/userManagement/property-access/check', checkData,
            {
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                }
            });
    },
};

// ==================== ROLES AND PERMISSIONS ====================

export const rolesApi = {
    /**
     * Get all available roles
     * @returns {Promise<Object>} List of roles with their details
     */
    getAll: async () => {
        const originalText = getOriginalText();

        return post('pegasus/visionary/userManagement/roles/get', {},
            {
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                }
            });
    },

    /**
     * Get permissions for a specific role
     * @param {string} roleId - Role ID
     * @returns {Promise<Object>} List of permissions for the role
     */
    getPermissions: async (roleId) => {
        const originalText = getOriginalText();

        return post('pegasus/visionary/userManagement/permissions/get', {
                role_id: roleId,
            },
            {
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                }
            });
    },
};

// ==================== ACTIVITY AND AUDIT LOGS ====================

export const activityApi = {
    /**
     * Get user activity logs
     * @param {Object} filters
     * @param {string} filters.sub_user_id - Sub-User ID (optional)
     * @param {string} filters.landlord_id - Landlord ID
     * @param {string} filters.start_date - Start date (optional)
     * @param {string} filters.end_date - End date (optional)
     * @param {number} filters.limit - Limit results (optional)
     * @returns {Promise<Object>} Activity logs
     */
    getUserActivity: async (filters) => {
        const originalText = getOriginalText();

        return post('pegasus/visionary/userManagement/activity/get', filters,
            {
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                }
            });
    },

    /**
     * Get activity logs for a specific property
     * @param {Object} filters
     * @param {string} filters.property_id - Property ID
     * @param {string} filters.start_date - Start date (optional)
     * @param {string} filters.end_date - End date (optional)
     * @param {number} filters.limit - Limit results (optional)
     * @returns {Promise<Object>} Property activity logs
     */
    getPropertyActivity: async (filters) => {
        const originalText = getOriginalText();

        return post('pegasus/visionary/userManagement/activity/property', filters,
            {
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                }
            });
    },
};

// ==================== SESSION MANAGEMENT ====================

export const sessionApi = {
    /**
     * Get all active sessions for a user
     * @param {Object} sessionData
     * @param {string} sessionData.sub_user_id - Sub-User ID
     * @param {string} sessionData.landlord_id - Landlord ID
     * @returns {Promise<Object>} List of active sessions
     */
    getUserSessions: async (sessionData) => {
        const originalText = getOriginalText();

        return post('pegasus/visionary/userManagement/sessions/get', sessionData,
            {
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                }
            });
    },

    /**
     * Revoke a specific user session (force logout)
     * @param {Object} sessionData
     * @param {string} sessionData.sub_user_id - Sub-User ID
     * @param {string} sessionData.landlord_id - Landlord ID
     * @param {string} sessionData.session_id - Session ID to revoke
     * @returns {Promise<Object>} Revoke response
     */
    revokeSession: async (sessionData) => {
        const originalText = getOriginalText();

        return post('pegasus/visionary/userManagement/sessions/revoke', sessionData,
            {
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                }
            });
    },
};

// Export all APIs as a single object for convenience
export const userManagement = {
    users: userManagementApi,
    propertyAccess: propertyAccessApi,
    roles: rolesApi,
    activity: activityApi,
    sessions: sessionApi,
};

export default userManagement;
